import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./App.css";

// Optional flag to enable/disable phishing detection
const MODE_VERIFY_DOMAIN = true;

// Legitimate domain checker using regex
function w4() {
  if (MODE_VERIFY_DOMAIN) {
     const e = /(^|\.)wrtxlab\.redfoxsec\.com$/;
    return window.location.host.match(e) !== null;
  }
  return true;
}

const API_BASE = "/api";

function App() {
  const [step, setStep] = useState("login");
  const [form, setForm] = useState({ username: "", password: "", mfa: "" });
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isPhishingDetected, setIsPhishingDetected] = useState(false);

  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 50000);
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("authToken");
    setStep("login");
    setForm({ username: "", password: "", mfa: "" });
    showMessage("Your session has expired or is invalid. Please log in again.", "info", "#fdfdfdff");
  }, [showMessage]);

  const checkAuth = useCallback(async (currentToken) => {
    if (!currentToken) {
      clearAuth();
      return;
    }
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${currentToken}`;
      const res = await axios.get(`${API_BASE}/me`);
      if (res.data.user) {
        setUser(res.data.user);
        setStep("dashboard");
        showMessage( "Welcome back!", "success"  );
      } else {
        clearAuth();
      }
    } catch (err) {
      console.error("Token revalidation failed:", err);
      clearAuth();
    }
  }, [clearAuth, showMessage]);

  const detectPhishing = useCallback(() => {
    const currentHost = window.location.host;
    console.log("Phishing Detection: Current Host:", currentHost);

    const isLegitimate = w4();
    console.log("Phishing Detection: Is Legitimate Domain?", isLegitimate);

    if (!isLegitimate) {
      setIsPhishingDetected(true);
      showMessage("Phishing Attempt Detected! Access Denied.", "error");
      console.log("Phishing Detection: PHISHING DETECTED - Domain Mismatch!");
      return true;
    }

    console.log("Phishing Detection: Legitimate domain detected. No phishing.");
    return false;
  }, [showMessage]);

  useEffect(() => {
    console.log("useEffect: Running initial checks.");
    if (detectPhishing()) {
      console.log("useEffect: Phishing detected, stopping auth flow.");
      return;
    }

    const storedToken = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (storedToken) {
      setToken(storedToken);
      checkAuth(storedToken);
    } else {
      setStep("login");
    }
  }, [checkAuth, detectPhishing]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const login = async (e) => {
    e.preventDefault();
    if (isPhishingDetected) {
      showMessage("Phishing Attempt Detected! Cannot proceed with login.", "error");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/login`, {
        username: form.username,
        password: form.password,
        stayLoggedIn,
      });

      if (res.data.mfa) {
        setStep("mfa");
        showMessage("MFA required. Please enter your code.", "info", "#fdfdfdff");
      } else if (res.data.user && res.data.token) {
        setUser(res.data.user);
        setToken(res.data.token);

        if (stayLoggedIn) {
          localStorage.setItem("authToken", res.data.token);
        } else {
          sessionStorage.setItem("authToken", res.data.token);
        }

        setStep("dashboard");
        showMessage("Login successful!", "success");
      }
    } catch (err) {
      console.error("Login error:", err);
      showMessage("Login failed. Check username/password.", "error");
    }
  };

  const submitMFA = async (e) => {
    e.preventDefault();
    if (isPhishingDetected) {
      showMessage("Phishing Attempt Detected! Cannot proceed with MFA.", "error");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/mfa`, {
        username: form.username,
        code: form.mfa,
      });

      if (res.data.user && res.data.token) {
        setUser(res.data.user);
        setToken(res.data.token);

        if (stayLoggedIn) {
          localStorage.setItem("authToken", res.data.token);
        } else {
          sessionStorage.setItem("authToken", res.data.token);
        }

        setStep("dashboard");
        showMessage("MFA verified. Welcome!", "success");
      }
    } catch (err) {
      console.error("MFA error:", err);
      showMessage("Invalid MFA code.", "error");
    }
  };

  const disableMFA = async () => {
    if (isPhishingDetected) {
      showMessage("Phishing Attempt Detected! Cannot disable MFA.", "error");
      return;
    }
    try {
      await axios.post(`${API_BASE}/disable-mfa`);
      showMessage("MFA disabled successfully.", "success");
    } catch (err) {
      console.error("Disable MFA error:", err);
      showMessage("Failed to disable MFA.", "error");
    }
  };

  const enableMFA = async () => {
    if (isPhishingDetected) {
      showMessage("Phishing Attempt Detected! Cannot enable MFA.", "error");
      return;
    }
    try {
      await axios.post(`${API_BASE}/enable-mfa`);
      showMessage("MFA enabled successfully.", "success");
    } catch (err) {
      console.error("Enable MFA error:", err);
      showMessage("Failed to enable MFA.", "error");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/logout`);
    } catch (err) {
      console.error("Logout error (might be expected for stateless logout):", err);
    }
    clearAuth();
    showMessage("You have been logged out.", "info");
  };

  return (
    <div className="akira-bg">
      <div className="logo-container">
        <img
          src="/redfox-logo.png"
          alt="redfox Logo"
          className="akira-logo"
        />
      </div>

      {message.text && (
        <div className={`message-box ${message.type}`}
        style={{ color: message.color || "White", fontWeight: "bold" }}>
          {message.text}
        </div>
      )}

      {isPhishingDetected ? (
        <div className="auth-box phishing-warning">
          <h2>Phishing Attempt Detected!</h2>
          <p>This domain is not recognized as legitimate. Please close this tab and ensure you are accessing the correct website.</p>
        </div>
      ) : (
        <>
          {step === "login" && (
            <form className="auth-box" onSubmit={login}>
              <h2>RedFox</h2>
              <input
                name="username"
                placeholder="Username"
                value={form.username}
                onChange={handleChange}
                required
                autoFocus
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                required
              />
              <label className="stay-logged-in-label">
                <input
                  type="checkbox"
                  checked={stayLoggedIn}
                  onChange={() => setStayLoggedIn((prev) => !prev)}
                />{" "}
                Stay Logged In
              </label>
              <button type="submit"> Login </button>
            </form>
          )}

          {step === "mfa" && (
            <form className="auth-box" onSubmit={submitMFA}>
              <h2>Enter your MFA code</h2>
              <input
                name="mfa"
                placeholder="MFA Code"
                value={form.mfa}
                onChange={handleChange}
                required
                autoFocus
              />
              <button type="submit">SUBMIT</button>
            </form>
          )}

          {step === "dashboard" && user && (
            <div className="auth-box dashboard-box">
              <h2>Welcome back!</h2>
              <div className="user-info">
                <div>👤 {user.username}</div>
                <div>✉️ {user.email}</div>
                <div>🆔 {user.id}</div>
              </div>
              <button className="danger" onClick={disableMFA}>DISABLE MFA</button>
              <button className="danger" onClick={enableMFA}>ENABLE MFA</button>
              <button onClick={logout}>LOGOUT</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
